import { lazy, Suspense } from 'react';
import { type RouteObject } from 'react-router';
import LinearProgress from '@mui/material/LinearProgress';
import { Layout } from './components/Layout';
import { Catalog } from './routes/Catalog';
import { RequireAuth } from './components/RequireAuth';
import { RootRedirect } from './components/RootRedirect';

// Public routes are eager (prerendered + fast first paint); authed/heavy
// routes (journey runner pulls in the form-engine) are code-split.
const Login = lazy(() => import('./routes/Login').then((m) => ({ default: m.Login })));
const Track = lazy(() => import('./routes/Track').then((m) => ({ default: m.Track })));
const Home = lazy(() => import('./routes/Home').then((m) => ({ default: m.Home })));
const JourneyRunner = lazy(() =>
  import('./routes/JourneyRunner').then((m) => ({ default: m.JourneyRunner })),
);
const MyApplications = lazy(() =>
  import('./routes/MyApplications').then((m) => ({ default: m.MyApplications })),
);
const ApplicationDetail = lazy(() =>
  import('./routes/ApplicationDetail').then((m) => ({ default: m.ApplicationDetail })),
);
const Profile = lazy(() => import('./routes/Profile').then((m) => ({ default: m.Profile })));

export const routes: RouteObject[] = [
  {
    // Full-screen hero page — deliberately outside the app chrome (no rail).
    path: '/auth/login',
    element: (
      <Suspense fallback={<LinearProgress aria-label="Loading" />}>
        <Login />
      </Suspense>
    ),
  },
  // The landing decision sits OUTSIDE the layout on purpose. As an index child
  // of <Layout /> it rendered the whole signed-out shell — rail, app bar, Sign in
  // button, footer — around a spinner for the length of the silent-restore round
  // trip, so an anonymous visitor watched a dashboard they have no access to
  // before being bounced to the login screen.
  { path: '/', element: <RootRedirect /> },
  {
    element: <Layout />,
    children: [
      {
        // The catalogue is behind the login now: applying is the only thing to
        // do here, and browsing services anonymously only to be bounced at the
        // apply button was the worse of the two experiences.
        path: 'services',
        element: (
          <RequireAuth>
            <Catalog />
          </RequireAuth>
        ),
      },
      // Tracking stays anonymous on purpose — the ARN plus a CAPTCHA is the
      // credential, so a citizen can check progress without an account.
      { path: 'track', element: <Track /> },
      {
        path: 'home',
        element: (
          <RequireAuth>
            <Home />
          </RequireAuth>
        ),
      },
      {
        path: 'apply/:serviceCode',
        element: (
          <RequireAuth>
            <JourneyRunner />
          </RequireAuth>
        ),
      },
      {
        path: 'applications',
        element: (
          <RequireAuth>
            <MyApplications />
          </RequireAuth>
        ),
      },
      {
        path: 'applications/:id',
        element: (
          <RequireAuth>
            <ApplicationDetail />
          </RequireAuth>
        ),
      },
      {
        path: 'profile',
        element: (
          <RequireAuth>
            <Profile />
          </RequireAuth>
        ),
      },
    ],
  },
  // Unknown paths land exactly like `/`: chromeless, decide, then redirect.
  { path: '*', element: <RootRedirect /> },
];
